var _sentryCollisionFreeGlobalObject = typeof window != "undefined" ? window : typeof global != "undefined" ? global : typeof self != "undefined" ? self : {};
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = undefined;
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"25OyKHGEKNq2q13DGJJtV"};
_sentryCollisionFreeGlobalObject["__sentryBasePath"] = undefined;
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "";

// This file configures the initialization of Sentry on the browser.
// The config you add here will be used whenever a page is visited.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from '@sentry/nextjs';

import { sanitizeSentryEvent } from './src/utils/sentryData';

const SENTRY_DSN = process.env.SENTRY_DSN || process.env.NEXT_PUBLIC_SENTRY_DSN;

const BLOCKED_FETCH_DOMAINS = [
  'trafficguard.ai',
  'app.brainfi.sh',
  'doubleclick.net',
  'google.com/rmkt/collect',
];
const FEDCM_ERROR_PATTERNS = [
  'fedcm get() rejects with networkerror',
  'error retrieving a token',
  'a network error occurred',
];

const getBreadcrumbText = (breadcrumb) => {
  if (typeof breadcrumb?.message === 'string') {
    return breadcrumb.message.toLowerCase();
  }

  const possibleText = [
    breadcrumb?.data?.url,
    breadcrumb?.data?.to,
    breadcrumb?.data?.message,
    breadcrumb?.data?.arguments?.[0],
  ].find((value) => typeof value === 'string');

  return typeof possibleText === 'string' ? possibleText.toLowerCase() : '';
};

Sentry.init({
  dsn: SENTRY_DSN,
  // Adjust this value in production, or use tracesSampler for greater control
  tracesSampleRate: 0,
  environment: process.env.NEXT_PUBLIC_SENTRY_ENV || process.env.NODE_ENV,
  beforeSend(event) {
    const sanitizedEvent = sanitizeSentryEvent(event);
    const exception = sanitizedEvent.exception?.values?.[0];
    const exceptionType = (exception?.type ?? '').toLowerCase();
    const exceptionValue = (exception?.value ?? '').toLowerCase();
    const breadcrumbs = sanitizedEvent.breadcrumbs ?? [];

    // Network-type browser errors we want to silence
    const isNetworkError =
      (exceptionType === 'typeerror' &&
        ['failed to fetch', 'load failed'].includes(exceptionValue)) ||
      (exceptionType === 'networkerror' &&
        FEDCM_ERROR_PATTERNS.some((pattern) => exceptionValue.includes(pattern)));

    if (!isNetworkError) {
      return sanitizedEvent;
    }

    // Look for blocked domains in breadcrumbs
    const hasBlockedDomain = breadcrumbs.some((breadcrumb) => {
      const possibleUrl = breadcrumb.data?.url ?? breadcrumb.data?.to ?? breadcrumb.message ?? '';

      return BLOCKED_FETCH_DOMAINS.some(
        (domain) => typeof possibleUrl === 'string' && possibleUrl.includes(domain)
      );
    });

    const hasFedCmTokenError = breadcrumbs.some((breadcrumb) =>
      FEDCM_ERROR_PATTERNS.some((pattern) => getBreadcrumbText(breadcrumb).includes(pattern))
    );

    if (hasBlockedDomain) {
      console.info('[Sentry] Dropped blocked fetch error', BLOCKED_FETCH_DOMAINS);

      return null;
    }

    if (hasFedCmTokenError) {
      console.info('[Sentry] Dropped FedCM token retrieval error');

      return null;
    }

    return sanitizedEvent;
  },
  // Note: if you want to override the automatic release value, do not set a
  // `release` value here - use the environment variable `SENTRY_RELEASE`, so
  // that it will also get attached to your source maps
});
